import { SignOut, UserCircle, SignIn, CreditCard } from 'phosphor-react';
import { useAuth } from '@/lib/auth';
import { ActionMenu, ActionList, Box } from '@primer/react';
import { useNavigate } from 'react-router-dom';
import { useAppContext } from '@/context/AppContext';
import { useRef, useState } from 'react';

import { ComponentType } from 'react';

type UserNavigationItem = {
  name: string;
  to: string;
  onClick?: () => void;
  icon?: ComponentType<React.SVGProps<SVGSVGElement>>;
};

export const UserNavigation = ({ userDetails }: { userDetails: any; menuClassName?: string }) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const triggerRef = useRef<HTMLButtonElement>(null);

  // Safe access to location pathname
  const currentPath = typeof window !== 'undefined' ? window.location.pathname : '';
  const showLogoWithFullName =
    currentPath.includes('/app/projects') && !currentPath.includes('/app/projects/');

  const { logout } = useAuth();
  const { setActiveTabModule } = useAppContext();
  const navigate = useNavigate();

  let userNavigation: UserNavigationItem[];

  if (!userDetails) {
    // If no userDetails, only show Log in option
    userNavigation = [
      {
        icon: SignIn,
        name: 'Log in',
        to: '/',
      },
    ];
  } else {
    // If userDetails exist, show all authenticated user options
    userNavigation = [
      {
        icon: CreditCard,
        name: 'Billing & Plans',
        to: '',
        onClick: () => navigate('/app/projects?settings', { state: { tab: 'Billing & Plans' } }),
      },
      {
        icon: UserCircle,
        name: 'Your Profile',
        to: showLogoWithFullName ? '/app/profile' : '',
        onClick: () => setActiveTabModule('profile'),
      },
      {
        icon: SignOut,
        name: 'Sign out',
        to: '',
        onClick: () => {
          logout();
        },
      },
    ];
  }

  return (
    <Box className="relative">
      <ActionMenu onOpenChange={setIsOpen} open={isOpen}>
        <ActionMenu.Anchor>
          <button
            ref={triggerRef}
            onClick={() => setIsOpen((prev) => !prev)}
            className={`flex items-center gap-2`}
            aria-label="Open menu"
          >
            {userDetails?.avatar_url ? (
              <img className="h-8 w-8 rounded-full" src={userDetails?.avatar_url} alt="" />
            ) : (
              <UserCircle size={34} weight="fill" />
            )}
          </button>
        </ActionMenu.Anchor>
        <ActionMenu.Overlay ignoreClickRefs={[triggerRef]}>
          <ActionList
            sx={{
              padding: '0px 0px',
            }}
          >
            {userDetails?.username && (
              <Box className={`flex items-center gap-2 py-3 px-4 text-sm border-b border-gray-600`}>
                @{userDetails.username}
              </Box>
            )}
            {userNavigation.map((item) => (
              <Box
                key={item.name}
                onClick={() => {
                  if (item.onClick) {
                    item.onClick();
                  }
                  if (item.to) {
                    navigate(item.to);
                  }
                  setIsOpen(false);
                }}
                className={`flex rounded-md items-center gap-2 py-3 px-4 hover:bg-gray-700 hover:text-white cursor-pointer text-xs`}
              >
                {item.icon && <item.icon className="h-4 w-4" aria-hidden="true" />}
                {item.name}
              </Box>
            ))}
          </ActionList>
        </ActionMenu.Overlay>
      </ActionMenu>
    </Box>
  );
};
